import React, { useState, useEffect, useRef } from "react";
// import ReactFlagsSelect from "react-flags-select";
// import { De } from "react-flags-select";
import { useStudioErstellen } from '../../store/studioErstellenStore';

import ErrorMessage from "./ErrorMessage";
import classes from "./Standort.module.css";
import GermanyFlag from "../../public/icons/germanEmoji.svg";
import { 
    createAddressValidationKey,
    validateAddressFields, 
    validateAddressWithGoogle 
} from "../../util/addressValidation";
import { useAddressAutocomplete } from "../../hooks/useAddressAutocomplete";

export const FirstStep = ({ validate }) => {
    const { studioData, updateStudioData } = useStudioErstellen();
    // const [selected, setSelected] = useState("");
    const [selected, setSelected] = useState(studioData.standort?.country || "Deutschland");
    const [errors, setErrors] = useState({
        country: "",
        street: "",
        postalCode: "",
        city: "",
        general: ""
    });
    const [isValidating, setIsValidating] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [streetInputValue, setStreetInputValue] = useState(studioData.standort?.street || "");
    const addressInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const didMountCountryEffectRef = useRef(false);

    const { suggestions, isLoading, searchAddress, selectPlace, clearSuggestions } = useAddressAutocomplete();

    const clearValidatedAddress = () => {
        updateStudioData('coordinates', null);
        updateStudioData('validatedAddress', null);
    };

    const [formData, setFormData] = useState({
        country: studioData.standort?.country || "",
        street: studioData.standort?.street || "",
        additionalAddress: studioData.standort?.additionalAddress || "",
        postalCode: studioData.standort?.postalCode || "",
        city: studioData.standort?.city || ""
    });

    // Initialize streetInputValue when formData changes externally
    useEffect(() => {
        if (formData.street !== streetInputValue && !showSuggestions) {
            setStreetInputValue(formData.street);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.street, showSuggestions]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for street input - trigger autocomplete
        if (name === "street") {
            const inputValue = value || "";
            setStreetInputValue(inputValue);
            if (inputValue.trim().length >= 3) {
                searchAddress(inputValue);
                setShowSuggestions(true);
            } else {
                clearSuggestions();
                setShowSuggestions(false);
            }
        }
        
        const newFormData = {
            ...formData,
            [name]: value
        };
        setFormData(newFormData);
        
        // Update context whenever form data changes
        updateStudioData('standort', {
            ...newFormData,
            country: selected
        });
        if (['street', 'postalCode', 'city', 'country'].includes(name)) {
            clearValidatedAddress();
        }
    };

    // Handle selection from autocomplete suggestions
    const handleSuggestionSelect = async (placeId) => {
        try {
            const addressData = await selectPlace(placeId);
            
            const newFormData = {
                street: addressData.street || formData.street,
                postalCode: addressData.postalCode || formData.postalCode,
                city: addressData.city || formData.city,
                additionalAddress: addressData.additionalAddress || formData.additionalAddress,
                country: addressData.country || selected
            };
            
            setFormData(newFormData);
            setStreetInputValue(newFormData.street);
            setShowSuggestions(false);
            clearSuggestions();
            
            // Update context with new data
            updateStudioData('standort', newFormData);
            
            // Store coordinates if available
            if (addressData.coordinates) {
                updateStudioData('coordinates', addressData.coordinates);
                updateStudioData('validatedAddress', {
                    source: 'google_places',
                    addressKey: createAddressValidationKey(newFormData),
                    coordinates: addressData.coordinates,
                    formattedAddress: addressData.formattedAddress || ''
                });
            } else {
                clearValidatedAddress();
            }
        } catch (error) {
            console.error('Error selecting place:', error);
        }
    };

    // Handle clicks outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                addressInputRef.current &&
                !addressInputRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        updateStudioData('standort', {
            ...formData,
            country: selected
        });
        if (didMountCountryEffectRef.current) {
            clearValidatedAddress();
        } else {
            didMountCountryEffectRef.current = true;
        }
    }, [selected]);

    const handleValidation = async () => {
        setIsValidating(true);
        
        // First, validate all fields locally
        const fieldErrors = validateAddressFields({
            country: selected,
            street: formData.street,
            postalCode: formData.postalCode,
            city: formData.city
        });
        
        // If there are any field-level errors, show them and stop
        if (Object.values(fieldErrors).some(error => error !== "")) {
            setErrors({ ...fieldErrors, general: "" });
            setIsValidating(false);
            return false;
        }
        
        // All field validations passed, now verify with Google API
        try {
            const googleValidation = await validateAddressWithGoogle({
                street: formData.street,
                postalCode: formData.postalCode,
                city: formData.city,
                country: selected
            });
            
            if (!googleValidation.isValid) {
                setErrors({
                    country: "",
                    street: "",
                    postalCode: "",
                    city: "",
                    general: googleValidation.error
                });
                setIsValidating(false);
                return false;
            }
            
            // If validation passed, update form data with normalized values from Google
            const normalizedFormData = {
                ...formData,
                street: googleValidation.normalizedStreet || formData.street,
                city: googleValidation.normalizedCity || formData.city,
                country: selected
            };
            setFormData(normalizedFormData);
            
            // Update context with normalized values
            updateStudioData('standort', normalizedFormData);
            
            // Store coordinates if available
            if (googleValidation.coordinates) {
                updateStudioData('coordinates', googleValidation.coordinates);
                updateStudioData('validatedAddress', {
                    source: 'google_geocode',
                    addressKey: createAddressValidationKey(normalizedFormData),
                    coordinates: googleValidation.coordinates,
                    formattedAddress: googleValidation.formattedAddress || ''
                });
            } else {
                clearValidatedAddress();
            }
            
            setErrors({
                country: "",
                street: "",
                postalCode: "",
                city: "",
                general: ""
            });
            setIsValidating(false);
            return true;
            
        } catch (error) {
            console.error('Validation error:', error);
            setErrors({
                country: "",
                street: "",
                postalCode: "",
                city: "",
                general: "Fehler bei der Adressüberprüfung. Bitte versuche es erneut."
            });
            setIsValidating(false);
            return false;
        }
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, selected, formData]);

    return (
        <div className={classes.mainContainer}>
            <div className={classes.titleContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Wo befindet sich dein Studio?</h2>
                <span className='slimRegularLarge'>Deine genaue Adresse wird anderen Nutzern nicht angezeigt.</span>
            </div>
            <div className={classes.bottomContainer}>
                <div className={classes.countryContainer}>
                    <h2>Land</h2>
                    {/* <ReactFlagsSelect
                        countries={["DE"]}
                        className="slimRegularLarge"
                        selected={selected}
                        onSelect={(code) => setSelected(code)}
                        placeholder="Land auswählen"
                    /> */}
                    <div className={classes.countryInput}>
                        <input
                            className="slimRegularLarge"
                            placeholder="Deutschland"
                            value={formData.country}
                            readOnly
                        />
                        <img src={GermanyFlag} alt="Flag of Germany" className={classes.flagIcon} />
                        {/* <De className={classes.flagIcon} /> */}
                    </div>
                    {errors.country && <ErrorMessage style={{marginTop: "20px"}}>{errors.country}</ErrorMessage>}
                </div>
                <div className={classes.addressContainer}>
                    <h2>Adresse</h2>
                    <div className={`${classes.inputsContainer}`}>
                        <div className={classes.autocompleteWrapper} ref={addressInputRef}>
                            <input
                                className="slimRegularLargeText"
                                placeholder="Straße und Hausnummer *"
                                name="street"
                                value={streetInputValue || ""}
                                onChange={handleInputChange}
                                onFocus={() => {
                                    if (streetInputValue && streetInputValue.trim().length >= 3) {
                                        searchAddress(streetInputValue);
                                        setShowSuggestions(true);
                                    }
                                }}
                                disabled={isValidating}
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <div className={classes.suggestionsContainer} ref={suggestionsRef}>
                                    {isLoading && (
                                        <div className={classes.suggestionItem}>Lädt...</div>
                                    )}
                                    {suggestions.map((suggestion) => (
                                        <div
                                            key={suggestion.place_id}
                                            className={classes.suggestionItem}
                                            onClick={() => handleSuggestionSelect(suggestion.place_id)}
                                        >
                                            {suggestion.description}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {errors.street && <ErrorMessage>{errors.street}</ErrorMessage>}
                        <input
                            className="slimRegularLarge"
                            placeholder="Adresszusatz:  Stockwerk, Gebäude"
                            name="additionalAddress"
                            value={formData.additionalAddress}
                            onChange={handleInputChange}
                            disabled={isValidating}
                        />
                        <input
                            className="slimRegularLarge"
                            placeholder="Postleitzahl *"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            disabled={isValidating}
                        />
                        {errors.postalCode && <ErrorMessage>{errors.postalCode}</ErrorMessage>}
                        <input
                            className="slimRegularLarge"
                            placeholder="Stadt/Ort *"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            disabled={isValidating}
                        />
                        {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                        {errors?.general && (
                            <ErrorMessage style={{textAlign: "center"}}>
                                {errors?.general}
                            </ErrorMessage>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FirstStep;
